import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import { AppShell } from '../components/layout/AppShell';
import { NotFoundState } from '../components/common/States';
import { Spinner } from '../components/ui/Spinner';
import { useBootPagePending } from './boot';
import { RequireAuth } from './RequireAuth';

/**
 * Public, high-traffic pages are in the main bundle. Everything behind the
 * session — and the heavier forms — is split out so a first visit stays light.
 */
const HomePage = lazy(() => import('../routes/HomePage'));
const AchievementsPage = lazy(() => import('../routes/AchievementsPage'));
const LeaderboardPage = lazy(() => import('../routes/LeaderboardPage'));
const CommunityPage = lazy(() => import('../routes/CommunityPage'));
const PostPage = lazy(() => import('../routes/PostPage'));
const PlayersPage = lazy(() => import('../routes/PlayersPage'));
const PlayerProfilePage = lazy(() => import('../routes/PlayerProfilePage'));
const FollowListPage = lazy(() => import('../routes/FollowListPage'));
const TermsPage = lazy(() => import('../routes/TermsPage'));
const TermsDocumentPage = lazy(() =>
  import('../routes/TermsPage').then((m) => ({ default: m.TermsDocumentPage })),
);
const GuidelinesPage = lazy(() => import('../routes/TermsPage').then((m) => ({ default: m.GuidelinesPage })));
const LoginPage = lazy(() => import('../routes/LoginPage'));
const RegisterPage = lazy(() => import('../routes/RegisterPage'));
const VerifyEmailPage = lazy(() => import('../routes/VerifyEmailPage'));
const CannotVerifyEmailPage = lazy(() => import('../routes/CannotVerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('../routes/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../routes/ResetPasswordPage'));
const AchievementDetailPage = lazy(() => import('../routes/AchievementDetailPage'));
const SpeedrunDetailPage = lazy(() => import('../routes/SpeedrunDetailPage'));
const SpeedrunsPage = lazy(() => import('../routes/SpeedrunsPage'));
const PointsPage = lazy(() => import('../routes/PointsPage'));
const NotificationsPage = lazy(() => import('../routes/NotificationsPage'));
const SettingsPage = lazy(() => import('../routes/SettingsPage'));

function PageFallback() {
  useBootPagePending();
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Spinner className="text-primary size-6" />
    </div>
  );
}

const page = (element: ReactNode) => <Suspense fallback={<PageFallback />}>{element}</Suspense>;

export const router = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
      { path: '/', element: page(<HomePage />) },
      { path: '/achievements', element: page(<AchievementsPage />) },
      { path: '/achievements/:code', element: page(<AchievementDetailPage />) },
      { path: '/leaderboard', element: page(<LeaderboardPage />) },
      { path: '/speedruns/:id', element: page(<SpeedrunDetailPage />) },
      { path: '/community', element: page(<CommunityPage />) },
      { path: '/community/:postId', element: page(<PostPage />) },
      { path: '/players', element: page(<PlayersPage />) },
      { path: '/players/:publicPlayerId', element: page(<PlayerProfilePage />) },
      { path: '/players/:publicPlayerId/followers', element: page(<FollowListPage kind="followers" />) },
      { path: '/players/:publicPlayerId/following', element: page(<FollowListPage kind="following" />) },
      { path: '/login', element: page(<LoginPage />) },
      { path: '/register', element: page(<RegisterPage />) },
      { path: '/verify-email', element: page(<VerifyEmailPage />) },
      { path: '/verify-email/help', element: page(<CannotVerifyEmailPage />) },
      { path: '/terms', element: page(<TermsPage />) },
      { path: '/terms/:code', element: page(<TermsDocumentPage />) },
      { path: '/guidelines', element: page(<GuidelinesPage />) },
      { path: '/forgot-password', element: page(<ForgotPasswordPage />) },
      { path: '/reset-password', element: page(<ResetPasswordPage />) },
      {
        element: <RequireAuth />,
        children: [
          { path: '/speedruns', element: page(<SpeedrunsPage />) },
          { path: '/points', element: page(<PointsPage />) },
          { path: '/notifications', element: page(<NotificationsPage />) },
          { path: '/settings', element: page(<SettingsPage />) },
          { path: '/settings/:tab', element: page(<SettingsPage />) },
        ],
      },
      { path: '*', element: <NotFoundState /> },
    ],
  },
]);
